import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, MapPin, Clock, Users, RefreshCw, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

export default function AiMatchSuggestions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = () => {
    setLoading(true);
    api.get("/matches/suggestions")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Sparkles size={14} className="animate-pulse text-golden" /> Finding your best matches...
      </div>
    );
  }

  if (!data?.suggestions?.length) return null;

  return (
    <div data-testid="ai-suggestions" className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-golden" />
          <span className="text-sm font-heading font-semibold">Suggested for you</span>
        </div>
        <button onClick={fetch} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="refresh-suggestions">
          <RefreshCw size={13} />
        </button>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {data.suggestions.map((s, i) => (
          <motion.div
            key={s.match_id || s._id || s.id || `${s.date}-${s.start_time}-${i}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.08 }}
            className="shrink-0 w-56 snap-start"
          >
            <div className="p-4 rounded-2xl bg-card border border-border/50 hover:border-coral/30 transition-all cursor-pointer card-lift h-full">
              {/* Players */}
              <div className="flex items-center gap-1.5 mb-3">
                {s.players?.slice(0, 2).map((name, j) => (
                  <div key={j} className="w-7 h-7 rounded-full bg-gradient-to-br from-coral/20 to-teal/20 flex items-center justify-center text-[10px] font-bold">
                    {name[0]?.toUpperCase()}
                  </div>
                ))}
                <span className="text-xs font-medium truncate ml-1">
                  {s.players?.join(" & ")}
                </span>
              </div>

              {/* Where & When */}
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <MapPin size={11} className="text-coral shrink-0" />
                  <span className="truncate">{s.club}</span>
                </div>
                {s.time_suggestion && (
                  <div className="flex items-center gap-1.5">
                    <Clock size={11} className="text-teal shrink-0" />
                    <span>{s.time_suggestion}</span>
                  </div>
                )}
              </div>

              {/* Mode pill */}
              <div className={`mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${
                s.mode === "competitive"
                  ? "bg-coral/10 text-coral"
                  : "bg-teal/10 text-teal"
              }`}>
                {s.mode}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
